import { useEffect, useRef, useState } from "react";
import { Save, Plus, PhoneIncoming, PhoneOutgoing, History, Clock, UserCircle2, Phone, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { initials, fmtDateTime, fmtTime, timeAgo, startManualDial } from "@/lib/app-utils";
import { useSoftphone } from "@/contexts/SoftphoneContext";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useNavigate } from "react-router-dom";
import JourneyTimeline from "@/components/JourneyTimeline";

function fmtSecs(s) {
  s = Math.max(0, Math.floor(s || 0));
  const m = Math.floor(s/60), sec = s % 60;
  if (m >= 60) { const h = Math.floor(m/60); return `${h}h ${m%60}m`; }
  return `${m}m ${sec.toString().padStart(2,"0")}s`;
}

export default function CustomerProfilePanel({ contactId, variant = "drawer", activeSession = null, onCall = false }) {
  const [contact, setContact] = useState(null);
  const [comments, setComments] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [cdrs, setCdrs] = useState([]);             // softphone CDRs for this contact
  const [newComment, setNewComment] = useState("");
  const [draft, setDraft] = useState({});
  const [nowTick, setNowTick] = useState(Date.now());
  const [calling, setCalling] = useState(false);
  const [waCalling, setWaCalling] = useState(false);
  const [screen, setScreen] = useState(null);        // resolved CRM screen (campaign-driven)
  const { flags: featureFlags } = useFeatureFlags();
  const waCallingEnabled = featureFlags?.wa_calling_enabled === true;
  const sp = useSoftphone();
  const nav = useNavigate();

  // Resolve the CRM screen for the agent's active campaign (→ default → fallback).
  useEffect(() => {
    api.get("/me/crm-screen")
      .then((r) => setScreen(r.data))
      .catch(() => setScreen(null));
  }, []);

  // Read/write a field value based on its binding.
  function fieldValue(f) {
    const b = f.binding;
    if (!b) return (draft.custom_values || {})[f.key] ?? "";
    if (b.startsWith("handle:")) return (draft.channel_handles || {})[b.split(":")[1]] ?? "";
    return draft[b] ?? "";
  }
  function setFieldValue(f, value) {
    const b = f.binding;
    if (!b) setDraft((d) => ({ ...d, custom_values: { ...(d.custom_values || {}), [f.key]: value } }));
    else if (b.startsWith("handle:")) {
      const ch = b.split(":")[1];
      setDraft((d) => ({ ...d, channel_handles: { ...(d.channel_handles || {}), [ch]: value } }));
    } else setDraft((d) => ({ ...d, [b]: value }));
  }

  async function placeCall() {
    const phone = (draft.phone || contact?.phone || "").trim();
    if (!phone) { toast.error("No phone number on file."); return; }
    setCalling(true);
    try {
      // Route CRM lead dials through the softphone's manual flow (gated by the
      // header campaign, labeled "Manual", recorded) AND open the workspace so
      // the lead-pop + omnichannel panels appear. When already embedded in the
      // workspace, the nav is a harmless no-op.
      startManualDial(phone, contact?.full_name || "", nav);
      toast.success(`Dialing ${contact?.full_name || phone}…`);
    } catch (e) { toast.error(formatErr(e)); } finally { setCalling(false); }
  }

  async function placeWaCall() {
    setWaCalling(true);
    try {
      const r = await api.post("/me/wa-call", { contact_id: contactId, peer_name: contact?.full_name || "" });
      const a = r.data?.action;
      if (a === "call") toast.success(r.data?.simulated ? "WhatsApp call placed (simulated in preview)" : "WhatsApp call placed");
      else if (a === "permission_requested") toast.info("Permission request sent — waiting for the customer to allow the call.");
      else toast(r.data?.detail || "Done");
    } catch (e) { toast.error(formatErr(e)); } finally { setWaCalling(false); }
  }

  useEffect(() => {
    if (!contactId) return;
    Promise.all([
      api.get(`/contacts/${contactId}`),
      api.get(`/contacts/${contactId}/comments`),
      api.get(`/callbacks`),
    ]).then(([c, cm, cb]) => {
      setContact(c.data);
      setDraft(c.data);
      setComments(cm.data);
      // Match callbacks by name OR by phone tail so wrap-up callbacks (which
      // store peer_name/peer rather than the CRM full_name) still surface.
      const norm = (s) => (s || "").replace(/\D/g, "").slice(-10);
      const cphone = norm(c.data?.phone);
      setCallbacks((cb.data || []).filter((x) =>
        x.customer === c.data.full_name || (cphone && norm(x.phone) === cphone)));
      // Pull softphone CDRs by phone (peer filter — handles formatting variants).
      if (c.data?.phone) {
        api.get(`/cdrs`, { params: { peer: c.data.phone, limit: 50 } })
          .then((r) => setCdrs(r.data?.items || []))
          .catch(() => {});
      }
    }).catch(() => toast.error("Couldn't load contact"));
  }, [contactId]);

  // Tick every second while an active call is on, so the duration prefix on
  // the in-progress note stays fresh.
  useEffect(() => {
    if (!activeSession || activeSession.state === "closed") return;
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [activeSession?.id, activeSession?.state]);

  function validateFields() {
    for (const f of (screen?.fields || [])) {
      const v = fieldValue(f);
      const empty = v === "" || v == null;
      if (f.required && empty) return `${f.label} is required`;
      if (!empty && f.type === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v))) return `${f.label} is not a valid email`;
      if (!empty && f.type === "phone" && !/^[+]?[\d\s()-]{6,}$/.test(String(v))) return `${f.label} is not a valid phone number`;
    }
    return null;
  }

  async function save() {
    const err = validateFields();
    if (err) { toast.error(err); return; }
    try {
      const updates = {};
      const scalars = ["full_name", "first_name", "last_name", "phone", "email",
        "company", "designation", "status", "address", "city", "state", "pincode",
        "disposition", "notes"];
      scalars.forEach((k) => { if (draft[k] !== undefined && draft[k] !== contact[k]) updates[k] = draft[k]; });
      if (JSON.stringify(draft.custom_values || {}) !== JSON.stringify(contact.custom_values || {})) updates.custom_values = draft.custom_values || {};
      if (JSON.stringify(draft.channel_handles || {}) !== JSON.stringify(contact.channel_handles || {})) updates.channel_handles = draft.channel_handles || {};
      if (Object.keys(updates).length === 0) { toast("No changes"); return; }
      const { data } = await api.patch(`/contacts/${contactId}`, updates);
      setContact(data); setDraft(data);
      toast.success("Profile saved");
      try { window.dispatchEvent(new CustomEvent("c7:contact-updated", { detail: { contact_id: contactId } })); } catch {}
    } catch { toast.error("Save failed"); }
  }

  // Auto-persist any edited CRM fields when the agent hangs up & disposes the
  // call (they may forget to click Save). Two triggers so it works in BOTH the
  // voice-only view (panel stays mounted → "c7:persist-crm" event fires) and
  // the omni view (panel unmounts on hangup → cleanup persists). Gated on
  // wasOnCallRef so closing a drawer outside a call never auto-saves.
  const draftRef = useRef(draft);
  const contactRef = useRef(contact);
  const wasOnCallRef = useRef(false);
  useEffect(() => { draftRef.current = draft; }, [draft]);
  useEffect(() => { contactRef.current = contact; }, [contact]);
  useEffect(() => { if (onCall) wasOnCallRef.current = true; }, [onCall]);
  useEffect(() => {
    function persistDirty() {
      const d = draftRef.current, c = contactRef.current;
      if (!c?.id || c.unknown) return;
      const updates = {};
      Object.keys(d || {}).forEach((k) => { if (d[k] !== c[k]) updates[k] = d[k]; });
      if (Object.keys(updates).length === 0) return;
      api.patch(`/contacts/${c.id}`, updates)
        .then(() => { try { toast.success("Customer info saved"); } catch {} })
        .catch(() => {});
    }
    window.addEventListener("c7:persist-crm", persistDirty);
    return () => {
      window.removeEventListener("c7:persist-crm", persistDirty);
      if (wasOnCallRef.current) persistDirty();  // torn down after a call → save edits
    };
  }, []);

  // Build a "call tag" to auto-prepend to the next note. Preference order:
  //   1. The currently-active session (if its peer matches this contact).
  //   2. The most recent CDR (within the last 10 minutes — i.e. just-wrapped).
  function buildCallTag() {
    // Match active session by phone (suffix-digit compare, mirrors backend regex)
    const norm = (s) => (s || "").replace(/\D/g, "").slice(-10);
    if (activeSession && norm(activeSession.peer) === norm(contact?.phone)) {
      const startedAt = activeSession.answered_at || activeSession.started_at;
      const elapsed = startedAt ? Math.max(0, Math.floor((nowTick - new Date(startedAt).getTime())/1000)) : 0;
      const dirLabel = activeSession.direction === "inbound" ? "Inbound" : "Outbound";
      return `[📞 ${dirLabel} call · ${fmtTime(activeSession.started_at)} · ${fmtSecs(elapsed)} so far · live]`;
    }
    // Most recent wrapped CDR (within 10 minutes)
    const recent = cdrs[0];
    if (recent) {
      const age = Date.now() - new Date(recent.started_at).getTime();
      if (age < 10 * 60 * 1000) {
        const dirLabel = recent.direction === "inbound" ? "Inbound" : "Outbound";
        return `[📞 ${dirLabel} call · ${fmtTime(recent.started_at)} · ${fmtSecs(recent.duration_seconds)}]`;
      }
    }
    return "";
  }
  const callTag = buildCallTag();

  async function addComment() {
    if (!newComment.trim()) return;
    const body = callTag ? `${callTag}\n${newComment.trim()}` : newComment.trim();
    try {
      const { data } = await api.post(`/contacts/${contactId}/comments`, { body });
      setComments([data, ...comments]);
      setNewComment("");
      toast.success("Note added" + (callTag ? " (tagged to call)" : ""));
    } catch { toast.error("Failed"); }
  }

  if (!contact) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  // Resolve the freshest "last contact" — newer of legacy contact.last_agent_at vs. most recent CDR.
  const lastCdr = cdrs[0] || null;
  let lastContactTs = contact.last_agent_at || null;
  let lastContactAgent = contact.last_agent || null;
  let lastContactDispo = contact.last_dispo || contact.disposition || null;
  let lastContactDuration = contact.last_dispo_duration || null;
  let lastContactDirection = null;
  if (lastCdr && (!lastContactTs || new Date(lastCdr.started_at) > new Date(lastContactTs))) {
    lastContactTs = lastCdr.started_at;
    lastContactAgent = lastCdr.agent_name || lastContactAgent;
    lastContactDispo = lastCdr.disposition_name || lastCdr.disposition_id || lastContactDispo;
    lastContactDuration = fmtSecs(lastCdr.billable_seconds || lastCdr.duration_seconds);
    lastContactDirection = lastCdr.direction;
  }
  const hasHistory = Boolean(lastContactTs || lastContactAgent || lastContactDispo);

  return (
    <div className={`@container space-y-3 ${variant === "drawer" ? "p-5" : "p-2"} min-w-0 max-w-full`}>
      {/* Hero (compact) */}
      <div className="rounded-xl bg-secondary text-secondary-foreground p-3.5 relative overflow-hidden min-w-0">
        <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-primary/30 blur-3xl pointer-events-none" />
        {/* Identity row + stats inline */}
        <div className="flex items-center gap-3 relative flex-wrap min-w-0">
          <Avatar className="h-9 w-9 ring-2 ring-white/20 shrink-0"><AvatarFallback className="bg-white/10 text-white font-bold text-xs">{initials(contact.full_name)}</AvatarFallback></Avatar>
          <div className="flex-1 min-w-0 basis-32">
            <div className="flex items-center gap-1.5 flex-wrap">
              <div className="font-display font-bold text-sm truncate">{contact.full_name}</div>
            </div>
            <div className="text-[11px] opacity-80 truncate">{contact.phone}</div>
          </div>
          <div className="flex items-center gap-1.5 text-right shrink-0 flex-wrap justify-end">
            <Button
              data-testid="crm-call-btn"
              onClick={placeCall}
              disabled={calling || onCall || !(draft.phone || contact?.phone)}
              size="sm"
              className="h-7 px-2.5 bg-emerald-500 hover:bg-emerald-600 text-white gap-1.5 shrink-0 disabled:opacity-50"
              title={onCall ? "You're already on a call" : (draft.phone || contact?.phone) ? `Call ${draft.phone || contact?.phone}` : "No phone on file"}
            >
              <Phone className="w-3.5 h-3.5"/> {calling ? "Dialing…" : "Call"}
            </Button>
            {waCallingEnabled && (draft.channel_handles?.whatsapp || contact?.channel_handles?.whatsapp) && (
              <Button
                data-testid="crm-wa-call-btn"
                onClick={placeWaCall}
                disabled={waCalling || onCall}
                size="sm"
                className="h-7 px-2.5 bg-[#25D366] hover:bg-[#1eb85a] text-white gap-1.5 shrink-0 disabled:opacity-50"
                title="Call this customer over WhatsApp"
              >
                <MessageCircle className="w-3.5 h-3.5"/> {waCalling ? "…" : "WhatsApp"}
              </Button>
            )}
            <MiniStat label="Since" value={contact.since || "—"} />
          </div>
        </div>
      </div>

      {/* Last Call Summary banner — prominent right above CRM Details */}
      <div
        data-testid="last-call-summary-banner"
        className={`rounded-xl border-2 p-3.5 ${
          hasHistory
            ? "border-emerald-300 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-950/30"
            : "border-dashed border-amber-300 dark:border-amber-800 bg-amber-50/40 dark:bg-amber-950/20"
        }`}
      >
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${
            hasHistory ? "bg-emerald-100 dark:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300"
                       : "bg-amber-100 dark:bg-amber-900/60 text-amber-700 dark:text-amber-300"
          }`}>
            {lastContactDirection === "inbound" ? <PhoneIncoming className="w-5 h-5" />
              : lastContactDirection === "outbound" ? <PhoneOutgoing className="w-5 h-5" />
              : <History className="w-5 h-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Last call summary</div>
            {hasHistory ? (
              <>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <Clock className="w-3.5 h-3.5 text-muted-foreground"/>
                  <span className="font-display font-bold text-sm" data-testid="last-call-relative">{lastContactTs ? timeAgo(lastContactTs) : "—"}</span>
                  {lastContactTs && <span className="text-xs text-muted-foreground">· {fmtDateTime(lastContactTs)}</span>}
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs">
                  {lastContactAgent && (
                    <span className="inline-flex items-center gap-1" data-testid="last-call-agent">
                      <UserCircle2 className="w-3.5 h-3.5 text-muted-foreground"/>
                      <span className="text-muted-foreground">by</span>
                      <span className="font-semibold">{lastContactAgent}</span>
                    </span>
                  )}
                  {lastContactDispo && (
                    <Badge variant="outline" className="text-[10px] gap-1" data-testid="last-call-dispo">
                      {lastContactDispo}
                    </Badge>
                  )}
                  {lastContactDuration && (
                    <span className="text-muted-foreground font-mono">{lastContactDuration}</span>
                  )}
                </div>
              </>
            ) : (
              <div className="text-sm font-medium mt-0.5 text-muted-foreground">No prior contact — this is a fresh lead.</div>
            )}
          </div>
        </div>
      </div>

      {/* Cards grid — 2 columns max on wide voice-only space (Notes/History are
          often sparse, so 3 columns looked empty). */}
      <div className="grid gap-3 items-start grid-cols-1 @[820px]:grid-cols-2">
      {/* CRM form — dynamic screen (campaign-driven) */}
      <Card title="CRM Details" subtitle={screen && screen.name ? screen.name : undefined} trailing={<Button data-testid="profile-save-btn" onClick={save} size="sm" className="rounded-md gap-1.5"><Save className="w-3.5 h-3.5" /> Save</Button>}>
        <div className="grid grid-cols-1 @[340px]/card:grid-cols-2 gap-3 min-w-0" data-testid="crm-fields">
          {(screen?.fields || []).map((f) => (
            <DynamicField key={f.key} field={f} value={fieldValue(f)} onChange={(v) => setFieldValue(f, v)} />
          ))}
        </div>
      </Card>

      {/* Notes (was Comment History) */}
      <Card title="Notes & Dispositions History" subtitle="Internal notes about this customer">
        {callTag && (
          <div data-testid="note-call-tag-preview" className="mb-2 px-2.5 py-1.5 rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 text-[11px] font-mono text-emerald-800 dark:text-emerald-300 truncate">
            {callTag}
            <span className="ml-2 text-emerald-700/70 dark:text-emerald-400/70 not-italic">— auto-prepended to your next note</span>
          </div>
        )}
        <div className="flex gap-2 mb-3">
          <Input data-testid="comment-input" value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Add a note about this customer…" onKeyDown={(e) => e.key === "Enter" && addComment()} />
          <Button data-testid="comment-add-btn" onClick={addComment} size="icon"><Plus className="w-4 h-4" /></Button>
        </div>
        <ul className="space-y-3">
          {comments.length === 0 && <li className="text-xs text-muted-foreground">No notes yet.</li>}
          {comments.map((c) => (
            <li key={c.id} className="text-sm border-l-2 border-secondary/40 pl-3">
              <div className="text-xs text-muted-foreground"><span className="font-semibold text-foreground">{c.agent}</span> · {fmtDateTime(c.ts)}</div>
              <div className="whitespace-pre-line">{c.body}</div>
            </li>
          ))}
        </ul>
      </Card>

      {/* History tabs */}
      <Card title="History" subtitle={`${cdrs.length} call(s) · ${callbacks.length} callback(s)`}>
        <Tabs defaultValue="calls">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger data-testid="tab-journey" value="journey" className="gap-1">
              Journey
            </TabsTrigger>
            <TabsTrigger data-testid="tab-calls" value="calls" className="gap-1">
              Calls {cdrs.length > 0 && <Badge variant="secondary" className="h-4 px-1 text-[10px]">{cdrs.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger data-testid="tab-dropped" value="dropped" className="gap-1">
              Dropped {cdrs.filter((c) => !c.answered).length > 0 && <Badge variant="secondary" className="h-4 px-1 text-[10px]">{cdrs.filter((c) => !c.answered).length}</Badge>}
            </TabsTrigger>
            <TabsTrigger data-testid="tab-callbacks" value="callbacks" className="gap-1">
              Callbacks {callbacks.length > 0 && <Badge variant="secondary" className="h-4 px-1 text-[10px]">{callbacks.length}</Badge>}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="journey" className="mt-3">
            <JourneyTimeline contactId={contactId} compact />
          </TabsContent>
          <TabsContent value="calls" className="mt-3">
            <CdrList rows={cdrs} />
          </TabsContent>
          <TabsContent value="dropped" className="mt-3">
            <CdrList rows={cdrs.filter((c) => !c.answered)} emptyText="No dropped / unanswered calls with this contact." />
          </TabsContent>
          <TabsContent value="callbacks" className="mt-3">
            <Mini rows={callbacks} cols={["scheduled_at", "agent", "status"]} formatters={{ scheduled_at: fmtDateTime }} />
          </TabsContent>
        </Tabs>
      </Card>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-white/5 rounded-lg p-3">
      <div className="text-overline opacity-80">{label}</div>
      <div className={`font-display font-bold text-lg mt-1 ${accent ? "text-amber-300" : ""}`}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  return (
    <div className="bg-white/5 rounded-md px-2 py-1 min-w-0 shrink">
      <div className="text-[9px] uppercase tracking-[0.18em] font-bold opacity-70 leading-none truncate">{label}</div>
      <div className={`font-display font-bold text-xs mt-0.5 truncate ${accent ? "text-amber-300" : ""}`}>{value}</div>
    </div>
  );
}

function Card({ title, subtitle, children, trailing }) {
  return (
    <div className="@container/card rounded-xl border border-border bg-card p-4 min-w-0 max-w-full overflow-hidden">
      <div className="flex items-start justify-between mb-3 gap-2 min-w-0">
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate">{title}</div>
          {subtitle && <div className="text-xs text-muted-foreground mt-0.5 truncate">{subtitle}</div>}
        </div>
        {trailing && <div className="shrink-0">{trailing}</div>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children, wide }) {
  return (
    <div className={`${wide ? "@[340px]/card:col-span-2" : ""} space-y-1 min-w-0`}>
      <Label className="text-overline text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function DynamicField({ field, value, onChange }) {
  const { type, label, options = [], required, placeholder, currency_code, key } = field;
  const tid = `crm-field-${key}`;
  const wide = type === "textarea";
  let control;
  if (type === "textarea") {
    control = <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full min-h-[72px]" data-testid={tid} />;
  } else if (type === "dropdown") {
    control = (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="w-full" data-testid={tid}><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    );
  } else if (type === "checkbox") {
    control = <div className="h-9 flex items-center"><Switch checked={!!value} onCheckedChange={onChange} data-testid={tid} /></div>;
  } else if (type === "date") {
    control = <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full" data-testid={tid} />;
  } else if (type === "currency") {
    control = (
      <div className="relative">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[11px] font-medium text-muted-foreground pointer-events-none">{currency_code || "USD"}</span>
        <Input type="number" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full pl-11" data-testid={tid} />
      </div>
    );
  } else {
    const htmlType = type === "number" ? "number" : type === "email" ? "email" : type === "phone" ? "tel" : "text";
    control = <Input type={htmlType} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full" data-testid={tid} />;
  }
  return <Field label={`${label}${required ? " *" : ""}`} wide={wide}>{control}</Field>;
}

function Mini({ rows, cols, formatters = {} }) {
  if (!rows?.length) return <div className="text-xs text-muted-foreground py-3">Nothing to show.</div>;
  return (
    <div className="space-y-1.5 text-xs">
      {rows.map((r, i) => (
        <div key={r.id || i} className="grid grid-cols-3 gap-2 px-2 py-2 rounded-md bg-muted/40">
          {cols.map((c) => <div key={c} className="truncate">{formatters[c] ? formatters[c](r[c]) : r[c]}</div>)}
        </div>
      ))}
    </div>
  );
}

function CdrList({ rows, emptyText = "No softphone calls with this contact yet." }) {
  if (!rows?.length) {
    return <div className="text-xs text-muted-foreground py-3">{emptyText}</div>;
  }
  const dirLabel = (c) => {
    if (c.direction === "inbound") return "Inbound";
    if (c.channel === "predictive") return "Outbound · Predictive";
    return "Outbound · Manual";
  };
  return (
    <ul className="space-y-1.5" data-testid="cdr-list">
      {rows.map((c) => {
        const Icon = c.direction === "inbound" ? PhoneIncoming : PhoneOutgoing;
        const answered = !!c.answered;
        return (
          <li key={c.id} className="flex items-start gap-2.5 px-2.5 py-2 rounded-md bg-muted/40 border border-transparent hover:border-border" data-testid={`cdr-row-${c.id}`}>
            <div className={`w-7 h-7 rounded-md grid place-items-center shrink-0 ${
              answered ? "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300"
                       : "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300"
            }`}>
              <Icon className="w-3.5 h-3.5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-xs">
                <span className="font-semibold truncate">{c.agent_name || "—"}</span>
                <span className="text-muted-foreground">·</span>
                <span className="text-muted-foreground" title={c.started_at}>{timeAgo(c.started_at)}</span>
                {c.recording && <Badge variant="destructive" className="h-4 px-1 text-[10px]">REC</Badge>}
                {c.conference && <Badge variant="outline" className="h-4 px-1 text-[10px]">conf</Badge>}
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 text-[11px] text-muted-foreground">
                <span className="font-medium text-foreground/80" data-testid={`cdr-dir-${c.id}`}>{dirLabel(c)}</span>
                <span>·</span>
                <span className="font-mono">{fmtSecs(c.duration_seconds)}</span>
                {!answered && <Badge variant="outline" className="h-4 px-1 text-[10px] border-amber-400 text-amber-700 dark:text-amber-300">no answer</Badge>}
                {c.billable_seconds != null && c.billable_seconds !== c.duration_seconds && (
                  <span className="font-mono opacity-70">({fmtSecs(c.billable_seconds)} billable)</span>
                )}
                {c.disposition_id && <Badge variant="outline" className="h-4 px-1 text-[10px]">{c.disposition_name || c.disposition_id}</Badge>}
              </div>
              {c.wrapup_notes && (
                <div className="mt-1 text-[11px] italic text-muted-foreground line-clamp-2 border-l-2 border-border pl-2">{c.wrapup_notes}</div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
